import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Truck, Printer, FileXls, HourglassMedium, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { StatusBadge } from "../components/badges";
import { useAuth } from "../context/AuthContext";

const RequestCard = ({ r }) => (
  <div data-testid={`request-card-${r.id}`}
    className={`rounded-md border p-4 ${r.status === "pending" ? "border-amber-300 bg-amber-50/60" : "border-red-200 bg-red-50/60"}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide ${r.status === "pending" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"}`}>
        {r.status === "pending" ? <HourglassMedium size={12} weight="bold" /> : <XCircle size={12} weight="bold" />}
        {r.status === "pending" ? "Awaiting approval" : "Rejected"}
      </span>
      <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}</span>
    </div>
    <p className="mt-2 text-sm font-medium text-slate-800">
      {r.items.map((i) => `${i.product_name} x${i.quantity}`).join(", ")}
      <span className="ml-2 font-mono text-xs text-slate-500">(total {r.total_quantity})</span>
    </p>
    {r.notes && <p className="mt-1 text-xs text-slate-500">Notes: {r.notes}</p>}
    {r.status === "rejected" && (
      <p className="mt-2 rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs text-red-700" data-testid={`request-reject-reason-${r.id}`}>
        Reason: {r.reject_reason} — by {r.reviewed_by}
      </p>
    )}
  </div>
);

const OrderCard = ({ o, download }) => {
  const d = o.dispatch || {};
  return (
    <div data-testid={`customer-order-${o.order_number}`} className="rounded-md border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-sm font-bold text-slate-900">{o.order_number}</span>
        <StatusBadge status={o.status} label={o.status_label} />
      </div>
      <p className="mt-2 text-sm text-slate-700">
        {o.items.map((i) => `${i.product_name} x${i.quantity}`).join(", ")}
        <span className="ml-2 font-mono text-xs text-slate-500">(total {o.total_quantity})</span>
      </p>
      <p className="mt-1 text-xs text-slate-400">Placed {new Date(o.created_at).toLocaleDateString()}</p>
      {(d.dispatch_date || d.vehicle_number) && (
        <div className="mt-3 rounded-md bg-slate-50 p-3 text-xs text-slate-600" data-testid={`customer-order-dispatch-${o.order_number}`}>
          <p className="flex items-center gap-1.5 font-semibold text-slate-700"><Truck size={13} weight="duotone" /> Dispatch details</p>
          <p className="mt-1">
            {d.dispatch_date && <>Date: <b>{d.dispatch_date}</b> · </>}
            {d.transporter_name && <>Transporter: <b>{d.transporter_name}</b> · </>}
            {d.vehicle_number && <>Vehicle: <b className="font-mono">{d.vehicle_number}</b> · </>}
            {d.lr_number && <>LR No: <b className="font-mono">{d.lr_number}</b></>}
            {d.driver_mobile && <> · Driver: <b className="font-mono">{d.driver_mobile}</b></>}
          </p>
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <button data-testid={`customer-order-pdf-${o.order_number}`}
          onClick={() => download(`/orders/${o.id}/slip`, `${o.order_number}.pdf`)}
          className="flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100">
          <Printer size={13} weight="duotone" /> PDF
        </button>
        <button data-testid={`customer-order-excel-${o.order_number}`}
          onClick={() => download(`/orders/${o.id}/export/excel`, `${o.order_number}.xlsx`)}
          className="flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100">
          <FileXls size={13} weight="duotone" /> Excel
        </button>
      </div>
    </div>
  );
};

export default function CustomerPortal() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    Promise.all([api.get("/customer/order-requests"), api.get("/customer/orders")])
      .then(([r, o]) => { setRequests(r.data.requests); setOrders(o.data.orders); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async (path, filename) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const open = requests.filter((r) => r.status !== "approved");

  return (
    <div data-testid="customer-portal-page" className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">My Orders</h1>
          <p className="mt-1 text-sm text-slate-500">Welcome, {user.name}. Track your orders and place new ones.</p>
        </div>
        <Link to="/portal/new" data-testid="portal-new-order-button"
          className="flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-slate-700">
          <Plus size={16} weight="bold" /> Place New Order
        </Link>
      </div>

      {loading ? (
        <p className="p-6 text-center text-sm text-slate-400">Loading…</p>
      ) : (
        <>
          {open.length > 0 && (
            <div className="space-y-3" data-testid="customer-requests-section">
              <h2 className="text-base font-bold text-slate-900 md:text-lg">Order Requests</h2>
              {open.map((r) => <RequestCard key={r.id} r={r} />)}
            </div>
          )}
          <div className="space-y-3" data-testid="customer-orders-section">
            <h2 className="text-base font-bold text-slate-900 md:text-lg">Confirmed Orders</h2>
            {orders.length === 0 ? (
              <p className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-400" data-testid="customer-orders-empty">
                No confirmed orders yet. Your requests appear here once approved.
              </p>
            ) : (
              orders.map((o) => <OrderCard key={o.id} o={o} download={download} />)
            )}
          </div>
        </>
      )}
    </div>
  );
}
